import {Autocomplete, Stack, TextField} from '@mui/material';
import {DesktopDatePicker} from '@mui/x-date-pickers';
import dayjs, {Dayjs} from 'dayjs';
import {useDictionary} from 'utils/hooks/useDictionary';
import {selectDictionaries} from 'slices';
import {useSelector} from 'react-redux';
import {CustomTags} from '@features/incident/listPopups/CustomTags';
import {useKeepOpen} from 'utils/hooks/useKeepOpen';
import {DT_FORMATS} from 'utils';
import {useEffect} from 'react';
import {useCasingColumnDictionary} from 'utils/hooks/useCasingColumnDictionary';
import i18n from 'i18n';

type FinalReportFiltersProps = {
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
    setDateFrom: (newValue: Dayjs | null) => void;
    setDateTo: (newValue: Dayjs | null) => void;
    setSelectedStages: (newValue: string[]) => void;
    setSelectedCasingColumns: (newValue: string[]) => void;
    selectedCasingColumns: string[];
    selectedStages: string[];
    setDisabled: (error: boolean) => void;
    setWarnings: (warnings: string[]) => void;
    dailyReportSelected: boolean;
};
export const FinalReportFilters = ({
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    setSelectedStages,
    setSelectedCasingColumns,
    selectedCasingColumns,
    selectedStages,
    setDisabled,
    setWarnings,
    dailyReportSelected,
}: FinalReportFiltersProps) => {
    const constructionStageApi = 'WellConstructionStage';
    useDictionary({code: constructionStageApi});
    const dictionaries = useSelector(selectDictionaries);
    const constructionStageNamesDictionary = dictionaries[constructionStageApi] || [];
    const {getCasingColumnNameByCode, casingColumnOptions} = useCasingColumnDictionary();
    const constructionStageNames = constructionStageNamesDictionary.map((el) => el.value);

    const dateError = dateFrom && dateTo && dateFrom.isAfter(dateTo);
    const maxDateRange = 31;
    const dateRangeError = dateFrom && dateTo && dateTo.diff(dateFrom, 'day') > maxDateRange;

    useEffect(() => {
        const warnings: string[] = [];
        if (dateError) {
            warnings.push(
                `${i18n.t('finalReportFilters.startDateCannotBeGreaterEndDate', {ns: 'supervising'})}`,
            );
        }
        if (dateRangeError && dailyReportSelected) {
            warnings.push(
                `${i18n.t('finalReportFilters.selectPeriodNoMoreThanToGenerate', {ns: 'supervising'})}`,
            );
        }
        setWarnings(warnings);
        setDisabled(Boolean(dateError || (dateRangeError && dailyReportSelected)));
    }, [dateError, dateRangeError, dailyReportSelected, setDisabled, setWarnings]);

    return (
        <Stack
            direction='row'
            justifyContent='center'
            alignItems='center'
            gap={1}
            marginBottom='8px'
        >
            <DesktopDatePicker
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={(newDate) => {
                    setDateFrom(newDate ? dayjs(newDate).startOf('day') : null);
                }}
                slotProps={{
                    textField: {
                        size: 'small',
                        sx: {
                            width: '150px',
                            fontSize: '14px',
                            flexShrink: 0,
                            position: 'relative',
                        },
                        error: !!dateError,
                    },
                    actionBar: {
                        actions: ['clear'],
                    },
                }}
                label={i18n.t('pretext.from', {ns: 'common'})}
                format={DT_FORMATS.defaultDate}
                maxDate={dateTo || undefined}
            />
            <DesktopDatePicker
                value={dateTo ? dayjs(dateTo) : null}
                onChange={(newDate) => {
                    if (newDate) {
                        setDateTo(newDate);
                    }
                }}
                slotProps={{
                    textField: {
                        size: 'small',
                        sx: {
                            width: '150px',
                            fontSize: '14px',
                            flexShrink: 0,
                            position: 'relative',
                        },
                        error: !!dateError,
                    },
                    actionBar: {
                        actions: ['clear'],
                    },
                }}
                label={i18n.t('pretext.to', {ns: 'common'})}
                format={DT_FORMATS.defaultDate}
                minDate={dateFrom || undefined}
            />
            <Autocomplete
                {...useKeepOpen()}
                multiple
                sx={{
                    width: '115px',
                    '& .MuiInputBase-input': {
                        minWidth: '1px !important',
                        flexGrow: 0,
                        flexShrink: 1,
                    },
                }}
                componentsProps={{
                    popper: {
                        style: {
                            width: '200px',
                        },
                    },
                }}
                disableClearable
                options={constructionStageNames}
                onChange={(event, values) => {
                    setSelectedStages(values);
                }}
                renderTags={() => (
                    <CustomTags
                        dataLength={selectedStages.length}
                        onClear={() => setSelectedStages([])}
                    />
                )}
                value={selectedStages}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            label={i18n.t('stage', {ns: 'common'})}
                            variant='outlined'
                            size='small'
                            InputProps={{
                                ...params.InputProps,
                            }}
                        />
                    );
                }}
            />
            <Autocomplete
                {...useKeepOpen()}
                multiple
                sx={{width: '195px'}}
                disableClearable
                options={casingColumnOptions}
                onChange={(event, values) => {
                    setSelectedCasingColumns(values);
                }}
                renderTags={() => (
                    <CustomTags
                        dataLength={selectedCasingColumns.length}
                        onClear={() => setSelectedCasingColumns([])}
                    />
                )}
                getOptionLabel={(option) => getCasingColumnNameByCode(option)}
                value={selectedCasingColumns}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            label={i18n.t('casingColumn', {ns: 'common'})}
                            variant='outlined'
                            size='small'
                        />
                    );
                }}
            />
        </Stack>
    );
};
